import React, { useState, useEffect } from 'react';
import { Modal, Heading, Text } from './ui';
import { chefSummaries } from '../services/chefSummaries';

const ChefSummariesModal = ({ 
    isOpen, 
    onClose, 
    onComplete,
    sessionId,
    user
}) => {
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [isListening, setIsListening] = useState(false);
    const [responses, setResponses] = useState({});
    const [isComplete, setIsComplete] = useState(false);
    const [timelinePreview, setTimelinePreview] = useState([]);
    const [userToken, setUserToken] = useState(null);

    useEffect(() => {
        if (isOpen && sessionId && user) {
            initializeUserToken();
            startQandA();
        } else {
            resetModal();
        }
    }, [isOpen, sessionId, user]);

    const initializeUserToken = async () => {
        if (user) {
            const token = await user.getIdToken();
            setUserToken(token);
        }
    };

    const startQandA = async () => {
        try {
            await chefSummaries.start({
                onProgress: (progressData) => {
                    setProgress({
                        current: progressData.currentQuestion,
                        total: progressData.totalQuestions
                    });
                    setCurrentQuestion(progressData.question);
                    setIsListening(true);
                },
                onResponse: async (response) => {
                    // Process response through AI if available
                    let formattedText = response.response;
                    if (sessionId && userToken) {
                        formattedText = await chefSummaries.processVoiceInput(
                            response.response,
                            sessionId,
                            userToken
                        );
                    }
                    
                    // Update responses
                    setResponses(prev => ({
                        ...prev,
                        [response.questionId]: {
                            ...response,
                            response: formattedText
                        }
                    }));
                    
                    // Update timeline preview
                    updateTimelinePreview(response.questionId, formattedText);
                },
                onComplete: (structuredReport) => {
                    setIsComplete(true);
                    setIsListening(false);
                    setCurrentQuestion("Thank you! I've gathered all the information. Let me prepare your session summary.");
                    
                    // Call onComplete with the structured report
                    if (onComplete) {
                        onComplete(structuredReport);
                    }
                }
            });
        } catch (error) {
            console.error('Error starting Chef Summaries Q&A:', error);
            onClose();
        }
    };

    const updateTimelinePreview = (questionId, response) => {
        // Add to timeline preview based on question type
        const timestamp = new Date().toISOString();
        
        if (questionId === 'key_moments' && response.trim()) {
            setTimelinePreview(prev => [
                ...prev,
                {
                    id: `preview_${Date.now()}`,
                    type: 'highlight',
                    description: response,
                    timestamp
                }
            ]);
        } else if (questionId === 'issues_challenges' && response.trim()) {
            setTimelinePreview(prev => [
                ...prev,
                {
                    id: `preview_${Date.now()}`,
                    type: 'issue',
                    description: response,
                    timestamp
                }
            ]);
        } else if (questionId === 'final_result' && response.trim()) {
            setTimelinePreview(prev => [
                ...prev,
                {
                    id: `preview_${Date.now()}`,
                    type: 'outcome',
                    description: response,
                    timestamp
                }
            ]);
        }
    };

    const resetModal = () => {
        setCurrentQuestion(null);
        setProgress({ current: 0, total: 0 });
        setIsListening(false);
        setResponses({});
        setIsComplete(false);
        setTimelinePreview([]);
        setUserToken(null);
        chefSummaries.stop();
    };

    const getTimelineIcon = (type) => {
        switch (type) {
            case 'highlight':
                return '✨';
            case 'issue':
                return '⚠️';
            case 'outcome':
                return '🍽️';
            default:
                return '📝';
        }
    };

    const handleClose = () => {
        chefSummaries.stop();
        resetModal();
        onClose();
    };

    const handleComplete = () => {
        handleClose();
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <Heading level={2} style={{ color: '#1b263b' }}>
                            Chef Summary
                        </Heading>
                        <Text className="text-neutral-900 opacity-60 mt-1">
                            Let me ask you a few questions about your cooking session
                        </Text>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                    >
                        ✕
                    </button>
                </div>

                {/* Progress Indicator */}
                {progress.total > 0 && (
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <Text className="text-sm font-mono uppercase tracking-wider text-neutral-900">
                                Question {progress.current} of {progress.total}
                            </Text>
                            <Text className="text-sm text-neutral-900 opacity-60">
                                {Math.round((progress.current / progress.total) * 100)}%
                            </Text>
                        </div>
                        <div className="w-full bg-neutral-200 h-2">
                            <div 
                                className="bg-neutral-900 h-2 transition-all duration-300"
                                style={{ width: `${(progress.current / progress.total) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Question Display */}
                <div className="mb-6">
                    {currentQuestion && (
                        <div className="bg-neutral-50 hairline p-4">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 bg-neutral-900 text-neutral-0 flex items-center justify-center font-mono text-sm">
                                    👨‍🍳
                                </div>
                                <div className="flex-1">
                                    <Text className="text-neutral-900 leading-relaxed">
                                        {currentQuestion}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Timeline Preview */}
                {timelinePreview.length > 0 && (
                    <div className="mb-6">
                        <Text className="text-sm font-medium mb-2 block text-neutral-900">
                            Session Summary Preview
                        </Text>
                        <div className="bg-neutral-50 hairline p-4 max-h-48 overflow-y-auto">
                            <div className="space-y-3">
                                {timelinePreview.map((entry) => (
                                    <div key={entry.id} className="flex items-start gap-2">
                                        <span className="text-lg flex-shrink-0">
                                            {getTimelineIcon(entry.type)}
                                        </span>
                                        <div className="flex-1">
                                            <Text className="text-sm text-neutral-900 leading-relaxed">
                                                {entry.description}
                                            </Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Listening Indicator */}
                {isListening && !isComplete && (
                    <div className="mb-6">
                        <div className="flex items-center justify-center gap-3 p-4 bg-neutral-50 hairline">
                            <div className="flex gap-1">
                                <div className="w-2 h-2 bg-neutral-900 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-neutral-900 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-neutral-900 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                            <Text className="text-sm font-mono uppercase tracking-wider text-neutral-900">
                                Listening...
                            </Text>
                        </div>
                    </div>
                )}

                {/* Completion Message */}
                {isComplete && (
                    <div className="mb-6">
                        <div className="bg-neutral-50 hairline p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-neutral-900 text-neutral-0 flex items-center justify-center font-mono text-sm">
                                    ✓
                                </div>
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                    Summary Complete
                                </Text>
                            </div>
                            <Text className="text-neutral-900 leading-relaxed">
                                I've gathered all the information from your cooking session. 
                                Your structured summary is ready for review and editing.
                            </Text>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                    {isComplete ? (
                        <button
                            onClick={handleComplete}
                            className="px-4 py-2 bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity duration-120 font-mono text-sm uppercase tracking-wider"
                        >
                            Review Summary
                        </button>
                    ) : (
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                        >
                            Skip Summary
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default ChefSummariesModal;

